import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { UserCog } from "lucide-react";
import { useApp } from "@/context/AppContext";

/** Self-edit dialog: name / fiscal code / address / phone. */
export default function ProfileEditDialog() {
  const { user, refreshUser, lang } = useApp();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", fiscal_code: "", address: "", phone: "" });

  useEffect(() => {
    if (!open || !user) return;
    setForm({
      name: user.name || "",
      fiscal_code: user.fiscal_code || "",
      address: user.address || "",
      phone: user.phone || "",
    });
  }, [open, user]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch("/auth/me", form);
      toast.success(lang === "it" ? "Profilo aggiornato" : "Profile updated");
      await refreshUser?.();
      setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const inputCls = "bg-white border-[#D8CFBB] text-[#1A1610] focus-visible:ring-[#B8965A]";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="profile-edit-btn"
          className="bg-transparent border border-[#B8965A]/40 text-[#B8965A] hover:bg-[#F5EDDA] rounded-full"
        >
          <UserCog className="h-4 w-4 me-2" />
          {lang === "it" ? "Modifica profilo" : "Edit profile"}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white border-[#E5E5E3] max-w-md" data-testid="profile-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-light" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            {lang === "it" ? "Modifica profilo" : "Edit profile"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div>
            <Label className="text-[#4A4A4A]">{lang === "it" ? "Nome" : "Name"}</Label>
            <Input data-testid="profile-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label className="text-[#4A4A4A]">{lang === "it" ? "Codice fiscale / P.IVA" : "Tax ID / VAT"}</Label>
            <Input data-testid="profile-fc" className={inputCls} value={form.fiscal_code} onChange={(e) => setForm({ ...form, fiscal_code: e.target.value })} />
          </div>
          <div>
            <Label className="text-[#4A4A4A]">{lang === "it" ? "Indirizzo" : "Address"}</Label>
            <Input data-testid="profile-address" className={inputCls} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
          <div>
            <Label className="text-[#4A4A4A]">{lang === "it" ? "Telefono" : "Phone"}</Label>
            <Input data-testid="profile-phone" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} className="text-[#6B6B6B]">
            {lang === "it" ? "Annulla" : "Cancel"}
          </Button>
          <Button onClick={submit} disabled={busy} data-testid="profile-save"
                  className="bg-[#B8965A] hover:bg-[#a08248] text-white rounded-full">
            {busy ? "…" : (lang === "it" ? "Salva" : "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
